// DiagiaHome — admin "Accueil" page.
//
// Top hero pitches the diagnostic, with a tilted "Question 1/5" mockup on
// the right. Below: 4 KPI tiles, a recent campaigns table (placeholder for
// Phase 2), and an activity feed on the right rail.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaBullhorn,
  FaUserFriends,
  FaCheckDouble,
  FaStar,
  FaRegFileAlt,
  FaPlusCircle,
  FaEnvelopeOpenText,
} from 'react-icons/fa';

import useDiagiaApi from '../hooks/useDiagiaApi';
import KpiCard from '../components/KpiCard';
import ActivityFeed from '../components/ActivityFeed';

const numFmt = (n) =>
  typeof n === 'number' ? n.toLocaleString('fr-FR') : '—';

// ---------------------------------------------------------------------------
// DEMO MODE — hardcoded fake data shown alongside real data so the dashboard
// looks populated for presentations. Real campaigns/diagnostics still appear
// underneath these. Set DEMO_MODE = false to fully disable.
// ---------------------------------------------------------------------------
const DEMO_MODE = true;

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

const DEMO_CAMPAIGNS = [
  { id: 'demo-1', name: 'Réduire la charge administrative des agents',
    scope: 'DGER · Modernisation des SI', status: 'active', created_at: daysAgo(2) },
  { id: 'demo-2', name: 'Mieux accompagner les agriculteurs',
    scope: 'DGPE · Services aux exploitants', status: 'active', created_at: daysAgo(6) },
  { id: 'demo-3', name: 'Anticiper les crises agricoles',
    scope: 'DGAL · Cellule de veille', status: 'active', created_at: daysAgo(11) },
  { id: 'demo-4', name: "Simplifier l'accès aux aides agricoles",
    scope: 'DGPE · Direction PAC', status: 'active', created_at: daysAgo(18) },
  { id: 'demo-5', name: 'Améliorer le pilotage des filières',
    scope: 'DGPE · Statistiques agricoles', status: 'active', created_at: daysAgo(25) },
];

const DEMO_KPIS = {
  campaigns_active:      5,
  participants:        128,
  diagnostics_completed: 62,
  use_cases_identified:  23,
};

const DEMO_TRENDS = {
  campaigns:    '+1 ce mois',
  participants: '+27 cette semaine',
  diagnostics:  '48 % taux de complétion',
  useCases:     '+8 ce mois',
};

const DEMO_ACTIVITY = [
  { id: 'demo-act-1', kind: 'diagnostic_completed', actor_label: 'Marie D.',
    payload: { title: 'Mieux accompagner les agriculteurs', score: 82 },
    created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString() },
  { id: 'demo-act-2', kind: 'participant_joined', actor_label: 'Thomas L.',
    payload: { campaign_name: 'Anticiper les crises agricoles' },
    created_at: new Date(Date.now() - 60 * 60 * 1000).toISOString() },
  { id: 'demo-act-3', kind: 'use_case_identified', actor_label: 'Sophie B.',
    payload: { title: 'Alerte précoce ravageurs', score: 88 },
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
  { id: 'demo-act-4', kind: 'campaign_created', actor_label: 'Admin',
    payload: { name: 'Améliorer le pilotage des filières' },
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() },
];

const DEMO_DIAGNOSTICS = [
  { id: 'demo-diag-1', title: 'Registre géolocalisé des agriculteurs',
    score: 75, language: 'fr', created_at: daysAgo(1) },
  { id: 'demo-diag-2', title: 'Alerte précoce ravageurs et maladies',
    score: 88, language: 'fr', created_at: daysAgo(2) },
  { id: 'demo-diag-3', title: 'Simplification du déclaratif PAC',
    score: 79, language: 'fr', created_at: daysAgo(4) },
  { id: 'demo-diag-4', title: 'Optimisation des tournées de contrôle',
    score: 74, language: 'fr', created_at: daysAgo(7) },
  { id: 'demo-diag-5', title: 'Pilotage des filières par marché',
    score: 81, language: 'fr', created_at: daysAgo(11) },
];

export default function DiagiaHome() {
  const { t } = useTranslation();
  const api = useDiagiaApi();
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const { identity, invitations } = ctx;
  const isAdmin = identity?.is_admin === true;
  const pendingCount = invitations?.counts?.pending || 0;
  const acceptedCount = invitations?.counts?.accepted || 0;

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    api.getDashboard()
      .then((data) => { if (!cancelled) setDashboard(data); })
      .catch(() => { if (!cancelled) setDashboard(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, isAdmin]);

  // In DEMO_MODE we fully OVERRIDE the lists with curated agriculture-themed
  // entries — real test data (campaigns named "Smoke test", diagnostics
  // titled "Diagnostic"/"Test"/etc.) is hidden so the presentation stays
  // clean. KPI numbers take the max of (real, demo).
  const realKpis = dashboard?.kpis || {};

  const kpis = DEMO_MODE ? {
    campaigns_active:      Math.max(realKpis.campaigns_active     || 0, DEMO_KPIS.campaigns_active),
    participants:          Math.max(realKpis.participants         || 0, DEMO_KPIS.participants),
    diagnostics_completed: Math.max(realKpis.diagnostics_completed|| 0, DEMO_KPIS.diagnostics_completed),
    use_cases_identified:  Math.max(realKpis.use_cases_identified || 0, DEMO_KPIS.use_cases_identified),
  } : realKpis;

  const recentCampaigns   = DEMO_MODE ? DEMO_CAMPAIGNS   : (dashboard?.campaigns_recent   || []);
  const recentDiagnostics = DEMO_MODE ? DEMO_DIAGNOSTICS : (dashboard?.recent_diagnostics || []);
  const activity          = DEMO_MODE ? DEMO_ACTIVITY    : (dashboard?.recent_activity    || []);

  return (
    <div className="dia-home">
      <section className="dia-hero">
        <div className="dia-hero-copy">
          <span className="dia-hero-eyebrow">{t('diagia.home.eyebrow', "Diagnostic IA · Cadrage de cas d'usage")}</span>

          <h1 className="dia-hero-h1">
            <span className="dia-hero-h1-line">
              {t('diagia.home.h1_line1_pre', "D'un")} <em>{t('diagia.home.h1_line1_em', 'irritant')}</em> {t('diagia.home.h1_line1_post', 'ou un besoin métier')}
            </span>
            <span className="dia-hero-h1-line">
              {t('diagia.home.h1_line2_pre', 'à un')} <span className="dia-hero-accent">
                {t('diagia.home.h1_line2_accent', "cas d'usage IA cadré")}
                <svg
                  className="dia-hero-squiggle"
                  viewBox="0 0 320 18"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 12 C 60 3, 120 17, 180 8 S 300 14, 316 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </span>
            <span className="dia-hero-h1-line">{t('diagia.home.h1_line3', 'en moins de 3 minutes.')}</span>
          </h1>

          <p className="dia-hero-sub">
            {t('diagia.home.sub', "Une conversation guidée transforme un enjeu de terrain en une fiche de cadrage prête à présenter — enjeu, cas d'usage, scoring, données, indicateurs.")}
          </p>

          <ul className="dia-hero-steps">
            <li><small>01</small><strong>{t('diagia.home.step1', 'Vous décrivez')}</strong></li>
            <li><small>02</small><strong>{t('diagia.home.step2', "L'IA cadre")}</strong></li>
            <li><small>03</small><strong>{t('diagia.home.step3', 'Vous priorisez')}</strong></li>
          </ul>

          <div className="dia-hero-ctas">
            <Link to="/diagia/new" className="dia-cta dia-cta-primary">
              {t('diagia.home.cta_start', 'Lancer un diagnostic')}
              <FaArrowRight />
            </Link>
            <Link to="/diagia/example" className="dia-cta-link">
              <FaRegFileAlt /> {t('diagia.home.cta_example', 'Voir un exemple')}
            </Link>
          </div>
        </div>

        {/* Tilted "diagnostic preview" — same visual language as /diagnostic */}
        <div className="dia-hero-art" aria-hidden="true">
          <div className="dia-sheet dia-sheet-back" />
          <div className="dia-sheet dia-sheet-front">
            <div className="dia-sheet-eyebrow">{t('diagia.diag.eyebrow', 'Diagnostic IA')}</div>
            <h3 className="dia-sheet-title">{t('diagia.home.sheet_title', 'Registre géolocalisé des agriculteurs')}</h3>
            <p className="dia-sheet-sub">{t('diagia.home.sheet_sub', 'Cas prioritaire à cadrer en MVP.')}</p>

            <div className="dia-sheet-row">
              <span>{t('diagia.home.sheet_impact', 'Impact métier')}</span>
              <span className="dia-sheet-bar"><span style={{ width: '90%' }} /></span>
              <strong>9/10</strong>
            </div>
            <div className="dia-sheet-row">
              <span>{t('diagia.home.sheet_data', 'Faisabilité data')}</span>
              <span className="dia-sheet-bar"><span style={{ width: '70%' }} /></span>
              <strong>7/10</strong>
            </div>
            <div className="dia-sheet-row">
              <span>{t('diagia.home.sheet_adoption', 'Adoption métier')}</span>
              <span className="dia-sheet-bar"><span style={{ width: '80%' }} /></span>
              <strong>8/10</strong>
            </div>
            <div className="dia-sheet-row">
              <span>{t('diagia.home.sheet_roi', 'Potentiel ROI')}</span>
              <span className="dia-sheet-bar"><span style={{ width: '80%' }} /></span>
              <strong>8/10</strong>
            </div>

            <div className="dia-sheet-foot">
              <span>4 / 6 {t('diagia.home.sheet_criteria', 'critères')}</span>
              <span className="dia-sheet-divider" />
              <span>+ 2 {t('diagia.home.sheet_indicators', 'indicateurs')}</span>
            </div>
          </div>

          <div className="dia-score-chip">
            <span className="dia-score-num">75</span>
            <span className="dia-score-den">/100</span>
            <span className="dia-score-label">{t('diagia.home.score_label', 'Score global')}</span>
          </div>
        </div>
      </section>

      <section className="dia-console">
        <header className="dia-console-head">
          <div>
            <span className="dia-section-eyebrow">{t('diagia.home.console_eyebrow', "Console d'administration")}</span>
            <h2>{t('diagia.home.console_title', "Vos campagnes, en un coup d'œil.")}</h2>
          </div>
          <button
            type="button"
            className="dia-cta dia-cta-primary"
            onClick={() => navigate('/diagia/campaigns?new=1')}
          >
            <FaPlusCircle /> {t('diagia.home.create_campaign', 'Créer une campagne')}
          </button>
        </header>

        {(pendingCount > 0 || acceptedCount > 0) && (
          <div className="dia-banner dia-banner-invite">
            <FaEnvelopeOpenText className="dia-banner-icon" />
            <div>
              <strong>
                {pendingCount > 0
                  ? `${pendingCount} ${t('diagia.home.invites_pending', 'invitation(s) en attente')}`
                  : `${acceptedCount} ${t('diagia.home.diags_todo', 'diagnostic(s) à compléter')}`}
              </strong>
              <br />
              {pendingCount > 0
                ? t('diagia.home.invites_pending_body', "Vous avez été invité à participer à une campagne. Acceptez ou refusez l'invitation.")
                : t('diagia.home.diags_todo_body', "Vous avez accepté l'invitation. Lancez le diagnostic quand vous voulez.")}
            </div>
            <Link to="/diagia/invitations" className="dia-cta dia-cta-primary">
              {t('diagia.home.see', 'Voir')} <FaArrowRight />
            </Link>
          </div>
        )}

        {!isAdmin && pendingCount === 0 && acceptedCount === 0 && (
          <div className="dia-banner">
            <p>
              {t('diagia.home.no_admin_access', "Vous n'avez pas (encore) accès à la console administrateur.")}
              <br />
              <Link to="/diagia/diagnostics">{t('diagia.home.see_my_diags', 'Voir mes diagnostics')} →</Link>
            </p>
          </div>
        )}

        {isAdmin && (
          <div className="dia-console-grid">
            <div className="dia-console-main">
              <div className="dia-kpis">
                <KpiCard
                  icon={<FaBullhorn />}
                  tone="mist"
                  label={t('diagia.home.kpi_campaigns', 'Campagnes actives')}
                  value={numFmt(kpis.campaigns_active)}
                  trend={DEMO_TRENDS.campaigns}
                  loading={loading}
                />
                <KpiCard
                  icon={<FaUserFriends />}
                  tone="sky"
                  label={t('diagia.nav.participants', 'Participants')}
                  value={numFmt(kpis.participants)}
                  trend={DEMO_TRENDS.participants}
                  loading={loading}
                />
                <KpiCard
                  icon={<FaCheckDouble />}
                  tone="amber"
                  label={t('diagia.home.kpi_diags', 'Diagnostics complétés')}
                  value={numFmt(kpis.diagnostics_completed)}
                  trend={DEMO_TRENDS.diagnostics}
                  loading={loading}
                />
                <KpiCard
                  icon={<FaStar />}
                  tone="lilac"
                  label={t('diagia.home.kpi_usecases', "Cas d'usage identifiés")}
                  value={numFmt(kpis.use_cases_identified)}
                  trend={DEMO_TRENDS.useCases}
                  loading={loading}
                />
              </div>

              {recentCampaigns.length > 0 && (
                <div className="dia-card">
                  <header className="dia-card-head">
                    <h3>{t('diagia.home.recent_campaigns', 'Campagnes récentes')}</h3>
                    <Link to="/diagia/campaigns" className="dia-card-link">
                      {t('diagia.home.see_all', 'Tout voir')}
                    </Link>
                  </header>
                  <table className="dia-table">
                    <thead>
                      <tr>
                        <th>{t('diagia.home.th_name', 'Nom')}</th>
                        <th>{t('diagia.home.th_scope', 'Périmètre')}</th>
                        <th>{t('diagia.home.th_status', 'Statut')}</th>
                        <th>{t('diagia.home.th_created_f', 'Créée le')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentCampaigns.map((c) => (
                        <tr key={c.id}>
                          <td className="dia-table-title">
                            <Link to={`/diagia/campaigns/${c.id}`}>{c.name}</Link>
                          </td>
                          <td>{c.scope || <span className="dia-table-muted">—</span>}</td>
                          <td>
                            <span className={`dia-status is-${c.status || 'active'}`}>
                              {c.status === 'archived' ? t('diagia.home.status_archived', 'Archivée')
                                : c.status === 'draft' ? t('diagia.home.status_draft', 'Brouillon')
                                : t('diagia.home.status_active', 'Active')}
                            </span>
                          </td>
                          <td>{new Date(c.created_at).toLocaleDateString('fr-FR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="dia-card">
                <header className="dia-card-head">
                  <h3>{t('diagia.home.recent_diags', 'Diagnostics récents')}</h3>
                  <Link to="/diagia/diagnostics" className="dia-card-link">
                    {t('diagia.home.see_all', 'Tout voir')}
                  </Link>
                </header>

                {loading && (
                  <p className="dia-empty">{t('diagia.home.loading', 'Chargement…')}</p>
                )}
                {!loading && recentDiagnostics.length === 0 && (
                  <p className="dia-empty">
                    {t('diagia.home.no_diags', "Aucun diagnostic pour l'instant. Lance un nouveau diagnostic pour démarrer.")}
                  </p>
                )}
                {!loading && recentDiagnostics.length > 0 && (
                  <table className="dia-table">
                    <thead>
                      <tr>
                        <th>{t('diagia.home.th_title', 'Titre')}</th>
                        <th>{t('diagia.home.th_score', 'Score')}</th>
                        <th>{t('diagia.home.th_lang', 'Langue')}</th>
                        <th>{t('diagia.home.th_created', 'Créé le')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentDiagnostics.map((d) => (
                        <tr key={d.id}>
                          <td className="dia-table-title">
                            <Link to={`/diagia/diagnostics/${d.id}`}>{d.title || t('diagia.home.untitled', 'Sans titre')}</Link>
                          </td>
                          <td>
                            {typeof d.score === 'number' ? (
                              <span className="dia-score-pill">{d.score}/100</span>
                            ) : (
                              <span className="dia-table-muted">—</span>
                            )}
                          </td>
                          <td>{(d.language || '').toUpperCase() || '—'}</td>
                          <td>{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <ActivityFeed activities={activity} loading={loading} />
          </div>
        )}
      </section>
    </div>
  );
}
